/**
 * Kurze Rueckmeldungen unten rechts.
 *
 * Uebernommen aus `finance-web/src/components/ui/Toast.tsx` — samt der Lehre,
 * die dort im Kommentar steht: Die Liste der Meldungen darf **nicht** im
 * Context-Wert stehen. Stand sie darin, wechselte der Wert bei jeder Meldung,
 * und wer `toast` in den Abhaengigkeiten eines Effekts hatte, meldete den
 * Fehler erneut, weil das Melden den Wert aenderte — eine Meldung baute die
 * naechste, bis sich Dutzende stapelten.
 */

import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { clsx } from 'clsx';
import { AlertTriangle, Check, Info, X } from 'lucide-react';

type Art = 'erfolg' | 'fehler' | 'hinweis';

interface Meldung {
  id: string;
  text: string;
  art: Art;
}

interface ToastContextType {
  erfolg: (text: string) => void;
  fehler: (text: string) => void;
  hinweis: (text: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [meldungen, setMeldungen] = useState<Meldung[]>([]);

  const entfernen = useCallback((id: string) => {
    setMeldungen((bisher) => bisher.filter((m) => m.id !== id));
  }, []);

  const melden = useCallback(
    (text: string, art: Art) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      setMeldungen((bisher) => [...bisher, { id, text, art }]);
      // Fehler bleiben laenger stehen — sie muss man lesen koennen.
      setTimeout(() => entfernen(id), art === 'fehler' ? 8000 : 4000);
    },
    [entfernen],
  );

  const wert = useMemo(
    () => ({
      erfolg: (text: string) => melden(text, 'erfolg'),
      fehler: (text: string) => melden(text, 'fehler'),
      hinweis: (text: string) => melden(text, 'hinweis'),
    }),
    [melden],
  );

  return (
    <ToastContext.Provider value={wert}>
      {children}
      <div className="fixed bottom-4 right-4 z-[60] flex flex-col gap-2">
        {meldungen.map((meldung) => (
          <ToastZeile key={meldung.id} meldung={meldung} onEntfernen={entfernen} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast gehört in einen ToastProvider');
  return context;
}

function ToastZeile({
  meldung,
  onEntfernen,
}: {
  meldung: Meldung;
  onEntfernen: (id: string) => void;
}) {
  const Symbol = meldung.art === 'erfolg' ? Check : meldung.art === 'fehler' ? AlertTriangle : Info;

  return (
    <div
      role="status"
      className={clsx(
        'flex max-w-sm animate-slide-in items-start gap-2.5 rounded-xl border px-4 py-3 shadow-soft-lg',
        meldung.art === 'erfolg' && 'border-success-200 bg-success-50 text-success-700',
        meldung.art === 'fehler' && 'border-danger-200 bg-danger-50 text-danger-700',
        meldung.art === 'hinweis' && 'border-slate-200 bg-white text-slate-700',
      )}
    >
      <Symbol size={16} className="mt-0.5 shrink-0" />
      <p className="text-sm">{meldung.text}</p>
      <button
        type="button"
        onClick={() => onEntfernen(meldung.id)}
        className="-mr-1 ml-1 shrink-0 opacity-60 transition-opacity hover:opacity-100"
        title="Ausblenden"
      >
        <X size={14} />
      </button>
    </div>
  );
}
